import { WithDisposable } from '@blocksuite/affine/global/lit';
import { unsafeCSSVar } from '@blocksuite/affine/shared/theme';
import { ShadowlessElement } from '@blocksuite/affine/std';
import { css, html, nothing } from 'lit';
import { property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

import { writeCopilotImageDragData } from '../../../utils/copilot-image-drag';
import {
  isLargeInlineImageDataUrl,
  isOmittedInlineImagePlaceholder,
} from '../../utils/blob-url-resolver';

type ChatImageItem = {
  key: string;
  src: string;
  omitted: boolean;
};

export class ChatContentImages extends WithDisposable(ShadowlessElement) {
  static override styles = css`
    .chat-content-images-row {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      gap: 8px;
      margin-bottom: 8px;
      max-width: 100%;
      overflow-x: auto;
      padding: 4px;
      scrollbar-width: auto;
    }

    .chat-content-images-row::-webkit-scrollbar {
      height: 4px;
    }

    .chat-content-images-row::-webkit-scrollbar-thumb {
      background-color: ${unsafeCSSVar('borderColor')};
      border-radius: 4px;
    }

    .chat-content-images-row::-webkit-scrollbar-track {
      background: transparent;
    }

    .chat-content-images-row img {
      max-width: 180px;
      max-height: 264px;
      object-fit: cover;
      border-radius: 8px;
      flex-shrink: 0;
      image-rendering: pixelated;
    }

    .chat-content-images-column {
      display: flex;
      gap: 12px;
      flex-direction: column;
      margin-bottom: 8px;
    }

    .chat-content-images-column .image-container {
      border-radius: 4px;
      overflow: hidden;
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 70%;
      max-width: 320px;
    }

    .chat-content-images-column .image-container img {
      max-width: 100%;
      max-height: 100%;
      width: auto;
      height: auto;
      image-rendering: pixelated;
    }

    .omitted-inline-image {
      align-items: center;
      border: 1px solid ${unsafeCSSVar('borderColor')};
      border-radius: 8px;
      color: var(--streamlain-text-secondary-color);
      display: flex;
      flex-shrink: 0;
      font-size: 12px;
      justify-content: center;
      line-height: 18px;
      min-height: 72px;
      padding: 12px;
      text-align: center;
      width: 180px;
    }
  `;

  @property({ attribute: false })
  accessor images: string[] = [];

  @property({ attribute: false })
  accessor layout: 'row' | 'column' = 'row';

  private getImageItems(): ChatImageItem[] {
    return this.images.map((src, index) => ({
      key: `${index}:${src.slice(0, 128)}`,
      src,
      omitted:
        isOmittedInlineImagePlaceholder(src) || isLargeInlineImageDataUrl(src),
    }));
  }

  private readonly handleImageDragStart = (
    event: DragEvent,
    item: ChatImageItem,
    index: number
  ) => {
    if (item.omitted || !event.dataTransfer) return;

    writeCopilotImageDragData(event.dataTransfer, item.src, index);
    event.dataTransfer.dropEffect = 'copy';

    const dragImage = event.currentTarget;
    if (dragImage instanceof HTMLElement) {
      event.dataTransfer.setDragImage(
        dragImage,
        dragImage.clientWidth / 2,
        dragImage.clientHeight / 2
      );
    }
  };

  private renderImage(item: ChatImageItem, index: number) {
    if (item.omitted) {
      return html`<div class="omitted-inline-image">
        Generated image too large to preview
      </div>`;
    }

    return html`<img
      src="${item.src}"
      loading="lazy"
      decoding="async"
      draggable="true"
      @dragstart=${(event: DragEvent) =>
        this.handleImageDragStart(event, item, index)}
    />`;
  }

  protected override render() {
    if (this.images.length === 0) {
      return nothing;
    }

    const images = this.getImageItems();

    if (this.layout === 'row') {
      return html`<div class="chat-content-images-row">
        ${repeat(
          images,
          image => image.key,
          (image, index) => this.renderImage(image, index)
        )}
      </div>`;
    } else {
      return html`<div class="chat-content-images-column">
        ${repeat(
          images,
          image => image.key,
          (image, index) =>
            html`<div class="image-container">
              ${this.renderImage(image, index)}
            </div>`
        )}
      </div>`;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'chat-content-images': ChatContentImages;
  }
}
